/**
 * The AI's outstanding suggestions for this enquiry's lines, or nothing.
 *
 * Reads feature `B`'s in-session extraction store, keyed by the enquiry's `mail_id`. **Session-
 * bound by design**: open the enquiry in a fresh tab, reload, or have a colleague open it, and
 * there is no extraction — this returns an empty map and every consumer renders exactly as it did
 * before suggestions existed. That is the required behaviour, not a degradation to apologise for:
 * the server has nowhere to persist these (filed as an ask), so pretending otherwise would show
 * affordances that cannot work.
 */

import { useCallback, useMemo, useSyncExternalStore } from 'react'
import { getPhase, subscribe } from '../../lib/api/extractionStore'
import { buildSuggestions, countOutstanding, type LineSuggestion } from './suggestions'
import type { EnquiryItem } from '../../lib/types'
import type { ExtractedLine } from '../../lib/api/useMailExtraction'

/** Module-level so the no-extraction path returns a STABLE reference. An inline `[]` here is a
 *  fresh array per render, which silently defeats the memo below — and the no-extraction case is
 *  the common one, not the edge case. */
const NO_LINES: ExtractedLine[] = []

export function useLineSuggestions(mailId: string | null, lines: EnquiryItem[]) {
  const phase = useSyncExternalStore(
    useCallback((listener: () => void) => (mailId ? subscribe(mailId, listener) : () => {}), [mailId]),
    useCallback(() => (mailId ? getPhase(mailId) : null), [mailId]),
  )

  const extracted = phase?.status === 'succeeded' ? phase.result.items : NO_LINES

  const byLine = useMemo(() => buildSuggestions(lines, extracted), [lines, extracted])
  const counts = useMemo(() => countOutstanding(byLine), [byLine])

  return { byLine, ...counts }
}

export type { LineSuggestion }
